import { ThreeHeadedDragonCardPosition, ThreeHeadedDragonOutcome, ThreeHeadedDragonSidePosition } from './three-headed-dragon.model';
import { CardPosition, Outcome } from '../../../models/communication/games.model';
import { GameBase } from '../../game-base';
import { HandState, SideState } from '../../models/game-view.model';

export class ThreeHeadedDragonGame extends GameBase {
    constructor() {
        super({
            sides: [
                {
                    position: ThreeHeadedDragonSidePosition.Dragon,
                    hands: [
                        {
                            label: $localize`GOLDEN`,
                            cards: [
                                {
                                    position: ThreeHeadedDragonCardPosition.GoldenDragonCard,
                                    isCompact: true
                                }
                            ]
                        },
                        {
                            label: $localize`BLACK`,
                            cards: [
                                {
                                    position: ThreeHeadedDragonCardPosition.BlackDragonCard,
                                    isCompact: true
                                }
                            ]
                        },
                        {
                            label: $localize`RED`,
                            cards: [
                                {
                                    position: ThreeHeadedDragonCardPosition.RedDragonCard,
                                    isCompact: true
                                }
                            ]
                        }
                    ]
                },
                {
                    position: ThreeHeadedDragonSidePosition.Tiger,
                    hands: [
                        {
                            label: $localize`TIGER`,
                            cards: [
                                {
                                    position: ThreeHeadedDragonCardPosition.TigerCard,
                                    isCompact: true
                                }
                            ]
                        }
                    ]
                }
            ]
        });
    }

    override getFirstCardPosition(): CardPosition {
        return ThreeHeadedDragonCardPosition.GoldenDragonCard;
    }

    override faceUpMessage(cardPosition: CardPosition): void {
        super.faceUpMessage(cardPosition);
        this.updateHand(this.getCardParentHand(cardPosition), HandState.Default);
    }

    override faceDownMessage(cardPosition: CardPosition): void {
        this.updateHands(HandState.Muted);
        super.faceDownMessage(cardPosition);
        this.updateHand(this.getCardParentHand(cardPosition), HandState.Default);
    }

    override getFieldLabel(cardPosition: CardPosition): string {
        switch (cardPosition as ThreeHeadedDragonCardPosition) {
            case ThreeHeadedDragonCardPosition.TigerCard: {
                return $localize`TIGER FIELD`;
            }
            case ThreeHeadedDragonCardPosition.GoldenDragonCard: {
                return $localize`GOLD DRAGON FIELD`;
            }
            case ThreeHeadedDragonCardPosition.BlackDragonCard: {
                return $localize`BLACK DRAGON FIELD`;
            }
            case ThreeHeadedDragonCardPosition.RedDragonCard: {
                return $localize`RED DRAGON FIELD`;
            }
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown card position: ${String(cardPosition)}`);
                return '';
        }
    }

    override getOutcomeLabel(outcome: Outcome): string {
        switch (outcome as ThreeHeadedDragonOutcome) {
            case ThreeHeadedDragonOutcome.DragonWin: {
                return $localize`DRAGON WIN`;
            }
            case ThreeHeadedDragonOutcome.TigerWin: {
                return $localize`TIGER WIN`;
            }
            case ThreeHeadedDragonOutcome.Tie: {
                return $localize`TIE`;
            }
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown outcome: ${String(outcome)}`);
                return '';
        }
    }

    override setOutcomeStates(outcome: Outcome, dragonOutcomes: Outcome[]): void {
        this.setOutcomeHandStates(outcome, dragonOutcomes);
        this.setOutcomeSideStates(outcome);
    }

    setOutcomeSideStates(outcome: Outcome): void {
        switch (outcome as ThreeHeadedDragonOutcome) {
            case ThreeHeadedDragonOutcome.Tie:
                this.updateSides(SideState.Tie);
                break;
            case ThreeHeadedDragonOutcome.TigerWin:
                this.updateSide(ThreeHeadedDragonSidePosition.Tiger, SideState.Win);
                break;
            case ThreeHeadedDragonOutcome.DragonWin:
                this.updateSide(ThreeHeadedDragonSidePosition.Dragon, SideState.Win);
        }
    }

    setOutcomeHandStates(outcome: Outcome, dragonOutcomes: Outcome[]): void {
        const dragonSide = this.getSide(ThreeHeadedDragonSidePosition.Dragon);
        const tigerSide = this.getSide(ThreeHeadedDragonSidePosition.Tiger);

        this.updateHands(HandState.Muted);

        if (outcome !== ThreeHeadedDragonOutcome.DragonWin) {
            this.updateHand(tigerSide.hands[0], HandState.Default);
        }

        if (outcome !== ThreeHeadedDragonOutcome.TigerWin) {
            for (const [handIndex, dragonOutcome] of dragonOutcomes.entries()) {
                if (dragonOutcome !== ThreeHeadedDragonOutcome.TigerWin && outcome === dragonOutcome) {
                    this.updateHand(dragonSide.hands[handIndex], HandState.Default);
                }
            }
        }
    }
}
